import { Vector3, Vector2, Quaternion, Object3D } from 'three';
import { IRotateArguments, IRotateControl } from './rotate-control-interface';

/**
 * minimum arguments needed to perform simple trackball rotation
 */
export interface ITrackballRotateArguments extends IRotateArguments {
    /**
     * point to rotate around
     */
    target?: Vector3;
}

/**
 * projection mode agnostic camera rotator
 *
 * [live demo](https://threejs.org/examples/misc_controls_trackball.html)
 *
 * example:
 * ```typescript
 * const camera = new Quick3dOrthographicCamera();
 * const pointToRotateAround = new Vector3(0,0,0); // origin
 * // set the camera 50 units in front of target and point towards it
 * camera.position.set(0,0,50);
 * camera.lookAt(pointToRotateAround);
 * const rotateControl = new GenericTrackballRotateControl();
 * // rotate horizontally 90 degrees
 * rotateControl.rotate(camera, {horizontalAngle: 1, rotateSpeed: Math.PI * 0.5, target: pointToRotateAround});
 * // rotate vertically 45 degrees
 * rotateControl.rotate(camera, {verticalAngle: 1, panSpeed: Math.PI * 0.25, target: pointToRotateAround});
 * // perform both of the above steps in a single call
 * rotateControl.rotate(camera, {horizontalAngle: Math.PI * 0.5, verticalAngle: Math.PI * 0.25, target: pointToRotateAround});
 * ```
 */
export class GenericTrackballRotateControl implements IRotateControl {
    private angleVector: Vector2;
    private axis: Vector3;
    private camSide: Vector3;
    private camUp: Vector3;
    private moveDir: Vector3;
    private previousTarget: Vector3;
    private quaternion: Quaternion;
    private toCam: Vector3;

    public constructor() {
        this.angleVector = new Vector2();
        this.axis = new Vector3();
        this.camSide = new Vector3();
        this.camUp = new Vector3();
        this.moveDir = new Vector3();
        this.previousTarget = new Vector3();
        this.quaternion = new Quaternion();
        this.toCam = new Vector3();
    }

    /**
     * rotates camera around target
     * @param rotateArguments.horizontalAngle defaulted to 0
     * @param rotateArguments.rotateSpeed defaulted to 1
     * @param rotateArguments.target defaulted to (0, 0, 0)
     * @param rotateArguments.verticalAngle defaulted to 0
     */
    public rotate(
        camera: Object3D,
        rotateArguments: ITrackballRotateArguments
    ): void {
        let {
            horizontalAngle = 0,
            rotateSpeed = 1,
            target = this.previousTarget,
            verticalAngle = 0,
        } = rotateArguments;

        const angle = this.angleVector
            .set(horizontalAngle, verticalAngle)
            .multiplyScalar(rotateSpeed)
            .length();

        if (angle !== 0) {
            // direction to camera from target
            this.toCam.copy(camera.position).sub(target);

            const distanceFromTarget = this.toCam.length();
            this.toCam.normalize();
            this.camUp.copy(camera.up);
            this.camSide.crossVectors(this.camUp, this.toCam).normalize();

            // get translation deltas
            this.camUp.multiplyScalar(this.angleVector.y);
            this.camSide.multiplyScalar(this.angleVector.x);

            this.moveDir.addVectors(this.camUp, this.camSide);

            // calculate axis object is rotating around
            this.axis.crossVectors(this.moveDir, this.toCam).normalize();
            this.axis.normalize();
            this.quaternion.setFromAxisAngle(this.axis, angle);

            camera.position
                .copy(this.toCam)
                .applyQuaternion(this.quaternion)
                .multiplyScalar(distanceFromTarget)
                .add(target);

            camera.up.applyQuaternion(this.quaternion);
            camera.lookAt(target);
        }

        this.previousTarget.copy(target);
    }
}
